<script setup>
import { ref, watch, computed } from 'vue';
import Draggable from 'vuedraggable';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  useMapGetter,
  useFunctionGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { t } = useI18n();
const store = useStore();
const { conversationSidebarItemsOrder, updateUISettings } = useUISettings();

const dragging = ref(false);

// v6.4.34: Installation-level sidebar section gating
const globalConfigStore = useMapGetter('globalConfig/get');
const sidebarSections = computed(
  () => globalConfigStore.value?.sidebarSections || {}
);

// v6.4.34: Account-level and user-level visibility checks
// (replicating the same logic from ContactPanel.vue)
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const storeGetters = useStoreGetters();
const currentAccount = computed(() => {
  return storeGetters['accounts/getAccount'].value(currentAccountId.value) || {};
});

// v6.4.34: AI Agents — SuperAdmin-only gating
// Same logic as ContactPanel.vue showAiAgentsSection
const showAiAgentsSection = computed(() => {
  const settings = currentAccount.value?.settings || {};
  // undefined (!== false) → restricted; false → everyone; true → restricted
  const superadminOnly = settings.ai_agents_superadmin_only !== false;
  if (superadminOnly) {
    return currentUser.value?.type === 'SuperAdmin';
  }
  return true;
});

// v6.4.34: Account-level feature flags
const isKanbanFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount.value?.(currentAccountId.value, 'kanban_board')
);
const isLinearFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount.value?.(currentAccountId.value, FEATURE_FLAGS.LINEAR)
);
const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);
const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value?.enabled
);
const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);
const isLinearClientIdConfigured = computed(() => !!linearIntegration.value?.id);

// v6.4.34: Unified visibility check — mirrors ContactPanel.vue template-level v-if guards
const shouldShowItem = (itemName) => {
  // Installation-level gating (SIDEBAR_SECTION_* config)
  const sections = sidebarSections.value;
  if (sections && Object.keys(sections).length > 0) {
    if (sections[itemName] === false) return false;
  }
  // Account/user-level feature guards (same as ContactPanel.vue template)
  switch (itemName) {
    case 'ai_agents':
      return showAiAgentsSection.value;
    case 'kanban_panel':
      return isKanbanFeatureEnabled.value;
    case 'linear_issues':
      return isLinearFeatureEnabled.value && isLinearClientIdConfigured.value;
    case 'shopify_orders':
      return isShopifyFeatureEnabled.value;
    default:
      return true;
  }
};

// v6.4.33: Guard to skip watcher rebuild when the change is triggered by our own save
let isLocalUpdate = false;

// v6.4.33: Map element names to i18n label keys
const sidebarItemLabels = {
  kanban_panel: 'SIDEBAR.KANBAN_BOARD_TITLE',
  conversation_actions: 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS',
  macros: 'CONVERSATION_SIDEBAR.ACCORDION.MACROS',
  ai_agents: 'CONVERSATION_SIDEBAR.ACCORDION.AI_AGENTS',
  conversation_info: 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO',
  contact_attributes: 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES',
  contact_notes: 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES',
  previous_conversation: 'CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION',
  conversation_participants: 'CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE',
  linear_issues: 'CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES',
  shopify_orders: 'CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS',
};

// Build the local items list from saved order, ensuring kanban_panel is included
const items = ref([]);

const buildItemsList = () => {
  const savedOrder = conversationSidebarItemsOrder.value;

  // Guard: null/undefined/empty from store → use defaults
  if (!savedOrder || !Array.isArray(savedOrder) || savedOrder.length === 0) {
    const defaultList = [
      { name: 'kanban_panel', enabled: true },
      ...DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER,
    ];

    // v6.4.34: Apply unified visibility filter
    items.value = defaultList.filter(item => shouldShowItem(item.name));
    return;
  }

  const copy = [...savedOrder];

  // Ensure kanban_panel is in the list
  if (!copy.find(item => item.name === 'kanban_panel')) {
    copy.unshift({ name: 'kanban_panel', enabled: true });
  }

  // v6.4.34: Apply unified visibility filter
  items.value = copy.filter(item => shouldShowItem(item.name));
};

// Initialize on mount
buildItemsList();

// Watch for external changes (e.g. another tab syncing via store)
watch(
  conversationSidebarItemsOrder,
  () => {
    // Skip rebuild if we just persisted this change ourselves
    if (isLocalUpdate) {
      isLocalUpdate = false;
      return;
    }
    buildItemsList();
  },
  { deep: true }
);

// Get translated label for each sidebar item
const getItemLabel = itemName => {
  const key = sidebarItemLabels[itemName];
  if (!key) return itemName;

  // v6.4.36: kanban_panel uses dynamic title from Kanban store settings
  // Same pattern as ContactPanel.vue: $t('SIDEBAR.KANBAN_BOARD_TITLE', { title })
  if (itemName === 'kanban_panel') {
    const kanbanTitle = store.getters['kanban/getKanbanTitle'] || 'Kanban - CRM';
    return t(key, { title: kanbanTitle });
  }

  return t(key);
};

const onDragEnd = () => {
  dragging.value = false;

  // Guard: skip watcher re-trigger from our own persist
  isLocalUpdate = true;

  // Persist the new order to ui_settings
  updateUISettings({
    conversation_sidebar_items_order: items.value,
  });

  useAlert(t('PROFILE_SETTINGS.FORM.SIDEBAR_ORDER.UPDATE_SUCCESS'));
};

const resetToDefault = () => {
  // Build default with kanban_panel at position 0
  const defaultItems = [
    { name: 'kanban_panel', enabled: true },
    ...DEFAULT_CONVERSATION_SIDEBAR_ITEMS_ORDER,
  ];

  // v6.4.34: Apply unified visibility filter (installation + account + user level)
  items.value = defaultItems.filter(item => shouldShowItem(item.name));

  // Guard: skip watcher re-trigger from our own persist
  isLocalUpdate = true;

  // Persist reset
  updateUISettings({
    conversation_sidebar_items_order: items.value,
  });

  useAlert(t('PROFILE_SETTINGS.FORM.SIDEBAR_ORDER.RESET_SUCCESS'));
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <Draggable
      :list="items"
      animation="200"
      ghost-class="ghost"
      handle=".sidebar-drag-handle"
      item-key="name"
      class="flex flex-col gap-2"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <template #item="{ element }">
        <div
          class="flex items-center gap-3 px-4 py-3 rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak cursor-grab select-none sidebar-drag-handle transition-colors hover:bg-n-slate-3 active:cursor-grabbing"
          :class="{ 'opacity-50': dragging }"
        >
          <!-- Grip icon (6 dots) -->
          <div class="flex flex-col gap-[2px] text-n-slate-8 flex-shrink-0">
            <div class="flex gap-[2px]">
              <span class="w-[3px] h-[3px] rounded-full bg-current" />
              <span class="w-[3px] h-[3px] rounded-full bg-current" />
            </div>
            <div class="flex gap-[2px]">
              <span class="w-[3px] h-[3px] rounded-full bg-current" />
              <span class="w-[3px] h-[3px] rounded-full bg-current" />
            </div>
            <div class="flex gap-[2px]">
              <span class="w-[3px] h-[3px] rounded-full bg-current" />
              <span class="w-[3px] h-[3px] rounded-full bg-current" />
            </div>
          </div>

          <!-- Item label -->
          <span class="text-sm font-medium text-n-slate-12">
            {{ getItemLabel(element.name) }}
          </span>
        </div>
      </template>
    </Draggable>

    <!-- Reset button -->
    <button
      class="self-start px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 rounded-md border border-n-weak hover:bg-n-slate-3 transition-colors"
      @click="resetToDefault"
    >
      {{ $t('PROFILE_SETTINGS.FORM.SIDEBAR_ORDER.RESET') }}
    </button>
  </div>
</template>

<style scoped>
.ghost {
  opacity: 0.5;
  background: rgb(var(--n-alpha-2));
  border: 1px dashed rgb(var(--n-blue-text));
}
</style>
